import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Globe, Lock, Plus, RefreshCw, Search, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { GithubRepoOptionDTO, IntegrationsDTO, JiraLinkDTO } from '@harness/shared';
import { GithubIcon, JiraIcon, PageHeader, Seg, Spinner } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import { relTime } from '../../lib/format';
import { qk } from '../../lib/keys';
import { useConnections, useIntegrations, type ProjectDetail } from '../../lib/queries';
import { toast } from '../../lib/store';
import { MfaRequired } from './ServerPage';

export const DEFAULT_JQL = 'assignee = currentUser() AND statusCategory != Done';

export function useGithubRepos(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['githubRepos', projectId],
    queryFn: () => api.get<GithubRepoOptionDTO[]>(`/api/projects/${projectId}/github/repos`),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/** Where Connect sends you: sign in with the provider, then straight back to this page. */
export const connectHref = (provider: 'github' | 'atlassian') => `/api/connections/${provider}/start?returnTo=${encodeURIComponent(window.location.pathname)}`;

/** GitHub for a project: your connected account (one click), or a token pasted for the project (advanced). */
export function GithubConnect({ projectId, info, onChange }: { projectId: string; info: IntegrationsDTO['github'] | undefined; onChange: () => Promise<unknown> }) {
  const conns = useConnections();
  const [showToken, setShowToken] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(false);
  const connect = async () => {
    setBusy(true);
    setMfa(false);
    try {
      await api.put(`/api/projects/${projectId}/github`, { token });
      setToken('');
      await onChange();
      toast('GitHub connected');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    try {
      await api.del(`/api/projects/${projectId}/github`);
      await onChange();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  if (info?.connected && info.via === 'account')
    return (
      <div className="tile tile-mint status-line" style={{ color: 'var(--color-text)' }}>
        <GithubIcon />
        <span style={{ flex: 1 }}>
          Using {conns.data?.github.account === info.login ? 'your' : 'the project creator’s'} GitHub account <b>@{info.login}</b> · pushes and PRs act as whoever approves them
        </span>
        <a className="btn btn-ghost" href="/settings/connections">
          Manage
        </a>
      </div>
    );
  if (info?.connected)
    return (
      <div className="tile tile-mint status-line" style={{ color: 'var(--color-text)' }}>
        <GithubIcon />
        <span style={{ flex: 1 }}>
          Connected as <b>{info.login}</b> · token {info.tokenHint} · contents, pull requests, metadata
        </span>
        <button className="btn btn-ghost" onClick={disconnect}>
          Disconnect
        </button>
      </div>
    );
  const appReady = conns.data?.github.available;
  if (appReady && !showToken)
    return (
      <div className="stack-sm">
        <div className="row" style={{ gap: 10 }}>
          <a className="btn btn-primary" href={connectHref('github')}>
            <GithubIcon />
            Connect GitHub
          </a>
          <button className="btn btn-ghost" onClick={() => setShowToken(true)}>
            Use a token instead
          </button>
        </div>
        <div className="hint">Sign in with GitHub once and every project of yours can use it. Repos come from where the Recite Me GitHub App is installed.</div>
      </div>
    );
  return (
    <div className="stack-sm">
      {!appReady && conns.data && <div className="hint">One-click Connect isn’t set up yet: an admin can add the GitHub App in Admin › Integrations. Meanwhile, paste a token.</div>}
      <div className="field">
        <label htmlFor="gh-token">Fine-grained personal access token</label>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <input id="gh-token" className="input mono" type="password" autoComplete="off" placeholder="github_pat_…" value={token} onChange={(e) => setToken(e.target.value)} />
          <button className="btn btn-primary" onClick={connect} disabled={busy || token.trim().length < 8}>
            {busy ? <Spinner /> : <GithubIcon />}
            Connect
          </button>
        </div>
        <div className="hint">Grant Contents (read/write), Pull requests (read/write) and Metadata (read) on the repos this project uses. No admin or force-push scope. Stored encrypted, scoped to this project.</div>
      </div>
      {mfa && <MfaRequired />}
    </div>
  );
}

export interface JiraDraft {
  siteUrl: string;
  projectKey: string;
  boardId: string;
  jqlFilter: string;
  authKind: 'cloud' | 'datacenter' | 'oauth';
  /** oauth: a site on your Atlassian connection. */
  cloudId: string;
  email: string;
  token: string;
  statusMapping: { inProgress: string; inReview: string };
}

export const jiraDraft = (j: JiraLinkDTO | null | undefined): JiraDraft => ({
  siteUrl: j?.siteUrl ?? '',
  projectKey: j?.projectKey ?? '',
  boardId: j?.boardId ?? '',
  jqlFilter: j?.jqlFilter ?? DEFAULT_JQL,
  authKind: j?.authKind ?? 'cloud',
  cloudId: j?.cloudId ?? '',
  email: j?.email ?? '',
  token: '',
  statusMapping: j?.statusMapping ?? { inProgress: 'In Progress', inReview: 'In Review' },
});

export function JiraFields({
  value: f,
  onChange,
  statuses,
  hasToken,
  projectId,
}: {
  value: JiraDraft;
  onChange: (v: JiraDraft) => void;
  statuses?: string[];
  hasToken?: boolean;
  projectId?: string;
}) {
  const set = (p: Partial<JiraDraft>) => onChange({ ...f, ...p });
  const conns = useConnections();
  const atl = conns.data?.atlassian;
  // New boards default to your Atlassian account when one-click Jira is available.
  useEffect(() => {
    if (atl?.available && !f.siteUrl && !f.cloudId && f.authKind === 'cloud') onChange({ ...f, authKind: 'oauth' });
  }, [atl?.available]); // eslint-disable-line react-hooks/exhaustive-deps
  const boards = useQuery({
    queryKey: ['jira-oauth-projects', projectId, f.cloudId],
    queryFn: () => api.get<{ key: string; name: string }[]>(`/api/projects/${projectId}/jira/oauth-projects?cloudId=${encodeURIComponent(f.cloudId)}`),
    enabled: f.authKind === 'oauth' && !!f.cloudId && !!projectId && !!atl?.connected,
    retry: false,
  });
  useEffect(() => {
    if (f.authKind === 'oauth' && !f.cloudId && atl?.sites.length === 1) onChange({ ...f, cloudId: atl.sites[0]!.id, siteUrl: atl.sites[0]!.url });
  }, [f.authKind, atl?.sites.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const statusSel = (k: 'inProgress' | 'inReview', id: string, label: string) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {statuses?.length ? (
        <select id={id} className="input" value={f.statusMapping[k]} onChange={(e) => set({ statusMapping: { ...f.statusMapping, [k]: e.target.value } })}>
          {!statuses.includes(f.statusMapping[k]) && <option value={f.statusMapping[k]}>{f.statusMapping[k]}</option>}
          {statuses.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      ) : (
        <input id={id} className="input" value={f.statusMapping[k]} onChange={(e) => set({ statusMapping: { ...f.statusMapping, [k]: e.target.value } })} />
      )}
    </div>
  );
  return (
    <div className="stack-sm">
      <div className="row">
        <span className="muted" style={{ fontSize: 13, marginRight: 4 }}>
          Jira
        </span>
        <Seg
          size="sm"
          value={f.authKind}
          onChange={(authKind) => set({ authKind })}
          options={[
            ...(atl?.available ? [{ value: 'oauth' as const, label: 'Your Atlassian account' }] : []),
            { value: 'cloud', label: 'Cloud + API token' },
            { value: 'datacenter', label: 'Data Center' },
          ]}
        />
      </div>
      {f.authKind === 'oauth' ? (
        atl?.connected ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label htmlFor="j-cloud">Jira site</label>
              <select
                id="j-cloud"
                className="input"
                value={f.cloudId}
                onChange={(e) => set({ cloudId: e.target.value, siteUrl: atl.sites.find((x) => x.id === e.target.value)?.url ?? '', projectKey: '' })}
              >
                <option value="">Choose a site…</option>
                {atl.sites.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} · {x.url.replace(/^https?:\/\//, '')}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="j-proj">Project</label>
              <select id="j-proj" className="input" value={f.projectKey} onChange={(e) => set({ projectKey: e.target.value })} disabled={!f.cloudId}>
                <option value="">{boards.isLoading ? 'Loading…' : 'Choose a project…'}</option>
                {(boards.data ?? []).map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.key} · {b.name}
                  </option>
                ))}
                {f.projectKey && !(boards.data ?? []).some((b) => b.key === f.projectKey) && <option value={f.projectKey}>{f.projectKey}</option>}
              </select>
            </div>
          </div>
        ) : (
          <div className="row" style={{ gap: 10 }}>
            <a className="btn btn-primary" href={connectHref('atlassian')}>
              <JiraIcon />
              Connect Jira
            </a>
            <span className="hint">Sign in with Atlassian once; then pick the site and project here.</span>
          </div>
        )
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 12 }}>
        <div className="field">
          <label htmlFor="j-site">Jira site</label>
          <input id="j-site" className="input" placeholder="yourteam.atlassian.net" value={f.siteUrl} onChange={(e) => set({ siteUrl: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="j-key">Project key</label>
          <input id="j-key" className="input" placeholder="LDG" value={f.projectKey} onChange={(e) => set({ projectKey: e.target.value.toUpperCase() })} />
        </div>
      </div>
      )}
      <div className="grid-2">
        {f.authKind === 'cloud' && (
          <div className="field">
            <label htmlFor="j-email">Atlassian account email</label>
            <input id="j-email" className="input" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
          </div>
        )}
        {f.authKind !== 'oauth' && (
          <div className="field">
            <label htmlFor="j-token">{f.authKind === 'cloud' ? 'API token' : 'Personal access token'}</label>
            <input id="j-token" className="input mono" type="password" autoComplete="off" placeholder={hasToken ? '•••••••• (saved — leave empty to keep)' : ''} value={f.token} onChange={(e) => set({ token: e.target.value })} />
          </div>
        )}
        <div className="field">
          <label htmlFor="j-board">Board ID (optional)</label>
          <input id="j-board" className="input" value={f.boardId} onChange={(e) => set({ boardId: e.target.value })} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="j-jql">JQL filter</label>
        <input id="j-jql" className="input mono" value={f.jqlFilter} onChange={(e) => set({ jqlFilter: e.target.value })} />
      </div>
      <div className="grid-2">
        {statusSel('inProgress', 'j-inprog', 'Status meaning “In progress”')}
        {statusSel('inReview', 'j-inrev', 'Status meaning “In review”')}
      </div>
    </div>
  );
}

/** Ready to save: oauth needs a site and project; token boards need a site, key and a token (or a saved one). */
export const jiraReady = (f: JiraDraft, hasToken?: boolean) =>
  f.authKind === 'oauth' ? !!f.cloudId && !!f.projectKey.trim() : !!f.siteUrl.trim() && !!f.projectKey.trim() && (!!hasToken || !!f.token.trim());

export function jiraBody(f: JiraDraft) {
  if (f.authKind === 'oauth')
    return {
      authKind: 'oauth' as const,
      cloudId: f.cloudId,
      projectKey: f.projectKey.trim(),
      boardId: f.boardId.trim() || undefined,
      jqlFilter: f.jqlFilter.trim() || DEFAULT_JQL,
      statusMapping: f.statusMapping,
    };
  return {
    siteUrl: f.siteUrl.trim(),
    projectKey: f.projectKey.trim(),
    boardId: f.boardId.trim() || undefined,
    jqlFilter: f.jqlFilter.trim() || DEFAULT_JQL,
    authKind: f.authKind,
    email: f.authKind === 'cloud' ? f.email.trim() || null : null,
    token: f.token.trim() || undefined,
    statusMapping: f.statusMapping,
  };
}

export default function IntegrationsPage({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const q = useIntegrations(project.id);
  const info = q.data;
  const repos = useGithubRepos(project.id, !!info?.github.connected);
  const conns = useConnections();
  const statuses = useQuery({
    queryKey: ['jiraStatuses', project.id],
    queryFn: () => api.get<string[]>(`/api/projects/${project.id}/jira/statuses`),
    enabled: !!info?.jira,
    retry: false,
  });
  const [jira, setJira] = useState<JiraDraft>(jiraDraft(null));
  const [savingJira, setSavingJira] = useState(false);
  const [jiraMfa, setJiraMfa] = useState(false);
  const [add, setAdd] = useState({ full: '', baseBranch: 'main', localPath: '', roleLabel: '' });
  const [syncing, setSyncing] = useState<string | null>(null);

  useEffect(() => setJira(jiraDraft(info?.jira)), [info?.jira]);

  const reload = () => qc.invalidateQueries({ queryKey: qk.integrations(project.id) });

  if (q.isLoading)
    return (
      <div className="settings-content">
        <Spinner />
      </div>
    );
  if (q.error)
    return (
      <div className="settings-content narrow">
        <PageHeader title="GitHub & Jira" />
        <div className="err">{q.error instanceof ApiError && q.error.status === 404 ? 'Integrations aren’t available on this server yet.' : errorText(q.error)}</div>
      </div>
    );

  const linkRepo = async () => {
    const [owner, repo] = add.full.trim().replace(/^https?:\/\/github\.com\//, '').replace(/\.git$/, '').split('/');
    if (!owner || !repo) return toast('Enter the repo as owner/name', 'error');
    try {
      await api.post(`/api/projects/${project.id}/repos`, { owner, repo, baseBranch: add.baseBranch.trim() || 'main', localPath: add.localPath.trim() || undefined, roleLabel: add.roleLabel.trim() });
      setAdd({ full: '', baseBranch: 'main', localPath: '', roleLabel: '' });
      await reload();
      toast(`${owner}/${repo} linked · cloning on the server`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const sync = async (id: string) => {
    setSyncing(id);
    try {
      await api.post(`/api/projects/${project.id}/repos/${id}/sync`);
      toast('Synced');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setSyncing(null);
      await reload();
    }
  };
  const unlink = async (id: string) => {
    try {
      await api.del(`/api/projects/${project.id}/repos/${id}`);
      await reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const patchRepo = async (id: string, body: Record<string, string>) => {
    try {
      await api.patch(`/api/projects/${project.id}/repos/${id}`, body);
      await reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const saveJira = async () => {
    setSavingJira(true);
    setJiraMfa(false);
    try {
      await api.put(`/api/projects/${project.id}/jira`, jiraBody(jira));
      await reload();
      await qc.invalidateQueries({ queryKey: qk.issues(project.id) });
      await qc.invalidateQueries({ queryKey: ['jiraStatuses', project.id] });
      toast('Jira connected · credentials checked');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setJiraMfa(true);
      else toast(errorText(e), 'error');
    } finally {
      setSavingJira(false);
    }
  };
  const unlinkJira = async () => {
    try {
      await api.del(`/api/projects/${project.id}/jira`);
      await reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const copy = (s: string) => void navigator.clipboard.writeText(s).then(() => toast('Copied'));
  const linked = new Set(info?.repos.map((r) => `${r.owner}/${r.repo}`));

  return (
    <div className="settings-content narrow">
      <PageHeader title="GitHub & Jira" sub="Repos the agents work across and the board they take tasks from. Every push and ticket update waits for your approval." />

      <section className="card card-pad stack-sm">
        <div className="section-title row" style={{ gap: 8 }}>
          <GithubIcon /> GitHub
        </div>
        <GithubConnect projectId={project.id} info={info?.github} onChange={reload} />
        <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Repository</th>
              <th>Path on server</th>
              <th style={{ width: 110 }}>Base</th>
              <th style={{ width: 120 }}>Role</th>
              <th>Last sync</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {(info?.repos ?? []).map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>
                  {r.owner}/{r.repo}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {r.localPath}
                </td>
                <td>
                  <InlineEdit value={r.baseBranch} onSave={(v) => patchRepo(r.id, { baseBranch: v })} label="Base branch" />
                </td>
                <td>
                  <InlineEdit value={r.roleLabel} onSave={(v) => patchRepo(r.id, { roleLabel: v })} label="Role" placeholder="role" />
                </td>
                <td style={{ fontSize: 12 }} className={r.syncStatus === 'error' ? 'err' : 'muted'} title={r.syncError ?? undefined}>
                  {r.syncStatus === 'syncing' || syncing === r.id ? 'syncing…' : r.syncStatus === 'error' ? `failed · ${r.syncError ?? ''}`.slice(0, 60) : r.lastSyncAt ? relTime(r.lastSyncAt) : 'never'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-icon sm" title="Sync" onClick={() => void sync(r.id)} disabled={syncing === r.id}>
                    {syncing === r.id ? <Spinner /> : <RefreshCw className="icon" />}
                  </button>
                  <button className="btn btn-icon sm" title="Unlink (files stay on the server)" onClick={() => void unlink(r.id)}>
                    <Trash className="icon" />
                  </button>
                </td>
              </tr>
            ))}
            {info?.repos.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No repos linked yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) 110px 120px auto', gap: 8, alignItems: 'end' }}>
          <div className="field">
            <label htmlFor="r-full">Repository</label>
            <RepoSearch
              value={add.full}
              onChange={(full) => setAdd({ ...add, full })}
              onPick={(r) => setAdd({ ...add, full: r.fullName, baseBranch: r.defaultBranch, localPath: `repos/${r.repo}` })}
              repos={repos.data}
              loading={repos.isFetching}
              error={repos.error ? errorText(repos.error) : null}
              onRefresh={() => void repos.refetch()}
              linked={linked}
              installUrl={info?.github.via === 'account' ? (conns.data?.github.installUrl ?? null) : null}
              enabled={!!info?.github.connected}
            />
          </div>
          <div className="field">
            <label htmlFor="r-path">Path on server</label>
            <input id="r-path" className="input mono" placeholder={`repos/${add.full.split('/')[1] ?? 'name'}`} value={add.localPath} onChange={(e) => setAdd({ ...add, localPath: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="r-base">Base</label>
            <input id="r-base" className="input" value={add.baseBranch} onChange={(e) => setAdd({ ...add, baseBranch: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="r-role">Role</label>
            <input id="r-role" className="input" placeholder="backend" value={add.roleLabel} onChange={(e) => setAdd({ ...add, roleLabel: e.target.value })} />
          </div>
          <button className="btn btn-secondary" onClick={linkRepo} disabled={!add.full.includes('/')}>
            <Plus className="icon" />
            Link
          </button>
        </div>
        {!info?.github.connected && <div className="hint">Connect GitHub first so the harness can clone, fetch and push.</div>}
      </section>

      <section className="card card-pad stack-sm">
        <div className="section-title row" style={{ gap: 8 }}>
          <JiraIcon /> Jira
        </div>
        <JiraFields value={jira} onChange={setJira} statuses={statuses.data} hasToken={info?.jira?.hasToken} projectId={project.id} />
        {jiraMfa && <MfaRequired />}
        <div className="row">
          <button className="btn btn-primary" onClick={saveJira} disabled={savingJira || !jiraReady(jira, info?.jira?.hasToken)}>
            {savingJira && <Spinner />}
            {info?.jira ? 'Save & test' : 'Connect Jira'}
          </button>
          {info?.jira && (
            <button className="btn btn-ghost" onClick={unlinkJira}>
              Disconnect
            </button>
          )}
        </div>
      </section>

      {info?.webhooks && (
        <section className="card card-pad stack-sm">
          <div className="section-title">Webhooks (optional)</div>
          <div className="muted" style={{ fontSize: 13 }}>
            Point these at the harness to refresh tasks and repo state as soon as something changes, instead of every 5 minutes.
          </div>
          <WebhookRow label="GitHub payload URL" value={info.webhooks.githubUrl} onCopy={copy} />
          {info.webhooks.githubSecret && <WebhookRow label="GitHub secret" value={info.webhooks.githubSecret} onCopy={copy} secret />}
          {info.webhooks.jiraUrl && <WebhookRow label="Jira webhook URL" value={info.webhooks.jiraUrl} onCopy={copy} secret />}
        </section>
      )}
    </div>
  );
}

function WebhookRow({ label, value, onCopy, secret }: { label: string; value: string; onCopy: (s: string) => void; secret?: boolean }) {
  const [show, setShow] = useState(!secret);
  return (
    <div className="field">
      <label>{label}</label>
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <input className="input mono" readOnly value={show ? value : '•'.repeat(24)} />
        {secret && (
          <button className="btn btn-secondary" onClick={() => setShow(!show)}>
            {show ? 'Hide' : 'Show'}
          </button>
        )}
        <button className="btn btn-icon btn-secondary" title="Copy" onClick={() => onCopy(value)}>
          <Copy className="icon" />
        </button>
      </div>
    </div>
  );
}

function InlineEdit({ value, onSave, label, placeholder }: { value: string; onSave: (v: string) => void; label: string; placeholder?: string }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input
      className="input"
      style={{ minHeight: 28, padding: '2px 8px', background: 'transparent', borderColor: 'transparent' }}
      aria-label={label}
      placeholder={placeholder}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== value && onSave(v.trim())}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

/**
 * Search your GitHub repos and pick one (fills owner/name, base branch and server path). With a GitHub
 * App connection the list is the repos where the App is installed; if there are none, say so and link
 * to installing it. Typing owner/name still works for anything not listed.
 */
function RepoSearch({
  value,
  onChange,
  onPick,
  repos,
  loading,
  error,
  onRefresh,
  linked,
  installUrl,
  enabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (r: GithubRepoOptionDTO) => void;
  repos: GithubRepoOptionDTO[] | undefined;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  linked: Set<string>;
  installUrl: string | null;
  enabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const words = value.toLowerCase().split(/[\s/]+/).filter(Boolean);
  const matches = (repos ?? []).filter((r) => words.every((w) => r.fullName.toLowerCase().includes(w))).slice(0, 50);
  useEffect(() => setSel(0), [value]);
  const pick = (r: GithubRepoOptionDTO) => {
    if (linked.has(r.fullName)) return;
    onPick(r);
    setOpen(false);
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search className="icon faint" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15 }} />
        <input
          id="r-full"
          className="input"
          style={{ paddingLeft: 36 }}
          placeholder={enabled ? 'Search your repositories…' : 'owner/name'}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls="repo-search-list"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => enabled && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (!open || !matches.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((i) => Math.min(matches.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((i) => Math.max(0, i - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pick(matches[sel]!);
            } else if (e.key === 'Escape') setOpen(false);
          }}
        />
      </div>
      {open && enabled && (
        <div className="menu repo-search" id="repo-search-list" role="listbox">
          {loading && !repos && (
            <div className="menu-head">
              <Spinner /> Loading your repositories…
            </div>
          )}
          {error && <div className="menu-head err">{error}</div>}
          {repos && repos.length === 0 && (
            <div className="repo-empty">
              <b>No repositories available yet.</b>
              <span>
                {installUrl
                  ? 'The Recite Harness GitHub App isn’t installed on any account or organisation you can access. Install it on the ReciteMe org and choose the repos, then refresh.'
                  : 'This GitHub connection can’t see any repositories.'}
              </span>
              {installUrl && (
                <a className="btn btn-primary btn-sm" href={installUrl} target="_blank" rel="noopener noreferrer" onMouseDown={(e) => e.preventDefault()}>
                  <GithubIcon /> Install the GitHub App
                </a>
              )}
            </div>
          )}
          {repos && repos.length > 0 && matches.length === 0 && <div className="menu-head">No match for “{value}”. You can still link it by typing owner/name.</div>}
          {matches.map((r, i) => {
            const done = linked.has(r.fullName);
            return (
              <button
                key={r.fullName}
                type="button"
                role="option"
                aria-selected={i === sel}
                className="menu-item"
                data-active={i === sel}
                disabled={done}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setSel(i)}
                onClick={() => pick(r)}
              >
                {r.private ? <Lock className="icon faint" /> : <Globe className="icon faint" />}
                <span className="ellipsis" style={{ flex: 1 }}>
                  <span className="faint">{r.owner}/</span>
                  <b>{r.repo}</b>
                </span>
                <span className="faint mono" style={{ fontSize: 12 }}>
                  {done ? 'linked' : r.defaultBranch}
                </span>
              </button>
            );
          })}
          {repos && (
            <div className="menu-foot" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="btn btn-ghost btn-sm" onMouseDown={(e) => e.preventDefault()} onClick={onRefresh}>
                <RefreshCw className="icon" /> Refresh
              </button>
              {installUrl && repos.length > 0 && (
                <a className="btn btn-ghost btn-sm" href={installUrl} target="_blank" rel="noopener noreferrer" onMouseDown={(e) => e.preventDefault()}>
                  Missing a repo? Configure the App
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
